import {
  Alert,
  Box,
  IconButton,
  LinearProgress,
  Typography,
} from "@mui/material";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useContext, useEffect, useRef, useState } from "react";
import GridPage from "components/layout/GridPage";
import useIsMobile from "components/hooks/useIsMobile";
import { ChevronLeftIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router";
import SearchResult from "components/search/SearchResult";
import { getCachedSearch, setCachedSearch, useSearch } from "services/search";
import { Filter } from "components/search/types";
import SearchBar from "components/common/SearchBar";
import SimpleFilters from "components/search/SimpleFilters";
import {
  generateSearchParamsWithFilters,
  parseSearchQueryUrl,
} from "components/search/utils";
import useTitle from "components/hooks/useTitle";
import { useSearchParams } from "react-router-dom";
import { CurrentUserContext } from "components/context/RequireAuth";
import { SearchResponse } from "protogen/search_service_pb";

const Constraints = ({
  filters,
  setFilters,
  loading,
}: {
  filters: Filter[];
  setFilters: (f: Filter[]) => void;
  loading: boolean;
}) => {
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      gap={"12px"}
      sx={{
        overflowX: "scroll",
        width: "100%",
        "&::-webkit-scrollbar": {
          display: "none",
        },
      }}
    >
      {filters
        .filter(
          (f) =>
            (f.presentationPrefix &&
              f.presentationValue &&
              f.field !== "family") ||
            f.field === "entity_type",
        )
        .map((filter) => (
          <div
            key={`${filter.field}:${filter.value}`}
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: "6px",
              padding: "8px 18px",
              borderRadius: "100px",
              background: "#E9F3F3",
            }}
          >
            <IconButton
              sx={{
                height: "30px",
                padding: "0px",
              }}
              disabled={loading}
              onClick={() => {
                setFilters(filters.filter((f) => f !== filter));
              }}
            >
              <XMarkIcon
                height={20}
                width={20}
                stroke="#198282"
                strokeWidth="2"
              />
            </IconButton>
            <Typography variant="body" color="#198282" fontWeight="600">
              {filter.presentationValue}
            </Typography>
          </div>
        ))}
    </Box>
  );
};

const filterSignature = (filters: Filter[]) =>
  JSON.stringify(
    filters.map((filter) => ({
      field: filter.field,
      operator: filter.operator,
      value: filter.value,
      presentationValue: filter.presentationValue,
    })),
  );

type Props = {};

export default ({}: Props) => {
  const isMobile = useIsMobile();
  const textFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const requestSequenceRef = useRef(0);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useContext(CurrentUserContext);
  const {
    searchQuery,
    referrer,
    filters: defaultFilters,
    chronological: defaultChronological,
    adminSearch: defaultAdminSearch,
  } = parseSearchQueryUrl(searchParams.toString());
  useTitle("Search");
  const [query, setQuery] = useState<string>(searchQuery);
  const { request, cancel, reset, error } = useSearch();
  const [visibleData, setVisibleData] = useState<SearchResponse | null>(null);
  const [searching, setSearching] = useState(false);
  const [adminSearch, setAdminSearch] = useState(defaultAdminSearch);
  const [chronological, setChronological] = useState(defaultChronological);
  const [appliedFilters, setAppliedFilters] =
    useState<Filter[]>(defaultFilters);
  const handleQuery = async () => {
    if (!query) return;
    const payload = {
      queryText: query,
      adminSearchMode: adminSearch,
      strictlyChronological: chronological,
      filters: appliedFilters.map((filter) => ({
        field: filter.field,
        operator: filter.operator,
        value: filter.value,
      })),
      maxResults: 50,
    };
    setSearchParams(
      generateSearchParamsWithFilters(query, appliedFilters, {
        referrer,
        chronological,
        adminSearch,
      }),
    );

    const requestSequence = ++requestSequenceRef.current;
    cancel();
    const cached = getCachedSearch(currentUser.ref, payload);
    if (cached) {
      reset();
      setVisibleData(cached);
      setSearching(false);
      return;
    }

    setSearching(true);
    const response = await request(payload);
    if (requestSequence !== requestSequenceRef.current) return;
    if (response) {
      setCachedSearch(currentUser.ref, payload, response);
      setVisibleData(response);
    }
    setSearching(false);
  };
  useEffect(() => {
    if (query) {
      void handleQuery();
    } else {
      requestSequenceRef.current += 1;
      cancel();
      reset();
      setVisibleData(null);
      setSearching(false);
      setSearchParams(
        generateSearchParamsWithFilters(null, appliedFilters, {
          referrer,
          chronological,
          adminSearch,
        }),
      );
      textFieldRef.current?.focus();
    }
  }, [query, adminSearch, appliedFilters, chronological]);
  const serializedSearchParams = searchParams.toString();
  useEffect(() => {
    const urlState = parseSearchQueryUrl(serializedSearchParams);
    setQuery((current) =>
      current === urlState.searchQuery ? current : urlState.searchQuery,
    );
    setAppliedFilters((current) =>
      filterSignature(current) === filterSignature(urlState.filters)
        ? current
        : urlState.filters,
    );
    setChronological(urlState.chronological);
    setAdminSearch(urlState.adminSearch);
  }, [serializedSearchParams]);
  const cancelRef = useRef(cancel);
  cancelRef.current = cancel;
  useEffect(
    () => () => {
      requestSequenceRef.current += 1;
      cancelRef.current();
    },
    [],
  );

  const resultCount = visibleData?.results.length || 0;
  const emailSourceSelected = appliedFilters.some(
    (filter) =>
      filter.field === "entity_type" &&
      filter.value.split(",").includes("email"),
  );

  return (
    <GridPage
      sx={{
        padding: isMobile ? "32px 24px" : "",
        margin: isMobile ? "" : "64px auto",
        maxWidth: "900px",
      }}
    >
      <Box
        sx={{
          flex: "1 1 100%",
          marginBottom: "32px",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "end",
        }}
      >
        <Box display="flex" flexDirection="column">
          {referrer && (
            <Box
              onClick={() => {
                navigate(-1);
              }}
              style={{ cursor: "pointer" }}
            >
              <Typography
                variant="body"
                sx={{
                  color: "#8E9598",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <ChevronLeftIcon
                  width="18px"
                  strokeWidth="2.5px"
                  style={{ marginRight: "4px" }}
                />
                {"Back"}
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant="display">Search</Typography>
          </Box>
        </Box>
        {!isMobile && (
          <SearchBar
            key={query}
            initialQuery={query}
            topics={[]}
            captureHotkeys={true}
            onQuery={(q) => {
              setQuery(q);
            }}
          />
        )}
      </Box>
      {isMobile && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <SearchBar
            key={query}
            fullWidth
            initialQuery={query}
            topics={[]}
            onQuery={(q) => {
              setQuery(q);
            }}
          />
        </Box>
      )}
      <Box
        display={query ? "flex" : "none"}
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          marginTop: isMobile ? "12px" : "",
        }}
      >
        <Box>
          <Typography variant="body">
            {searching
              ? `Searching for "${query}"…`
              : visibleData
                ? `${
                    resultCount === 50 ? "Showing top 50" : `${resultCount}`
                  } result${resultCount === 1 ? "" : "s"} for "${query}"`
                : `Search results for "${query}"`}
          </Typography>
        </Box>
      </Box>
      <Box
        display="flex"
        sx={{
          marginTop: isMobile ? "12px" : "24px",
          marginBottom: isMobile ? "12px" : "24px",
          flexWrap: "wrap",
          ...(isMobile
            ? {
                gap: "12px",
                flexDirection: "column",
              }
            : {
                gap: "12px",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }),
        }}
      >
        <SimpleFilters
          updateChronological={setChronological}
          updateAdminSearch={(b) => {
            setAppliedFilters(
              appliedFilters.filter((f) => !b || f.field !== "family"),
            );
            setAdminSearch(b);
          }}
          filters={appliedFilters}
          setFilters={setAppliedFilters}
          chronological={chronological}
          adminSearch={adminSearch}
        />
        <Constraints
          filters={appliedFilters}
          setFilters={setAppliedFilters}
          loading={searching}
        />
      </Box>
      {emailSourceSelected && (
        <Typography
          variant="bodySmall"
          color="#6B6E7B"
          sx={{ marginTop: "-12px", marginBottom: "16px" }}
        >
          Email tip: use from:, to:, or with: followed by an email address.
        </Typography>
      )}
      <Box
        display="flex"
        flexDirection="column"
        gap="16px"
        sx={{ width: "100%" }}
      >
        {searching && <LinearProgress aria-label="Updating search results" />}
        {error && (
          <Alert variant="outlined" severity="error">
            Search failed: {error}
          </Alert>
        )}
        {(visibleData?.results || []).map((result) => (
          <SearchResult
            key={`${result.entityType}:${result.entityRef}`}
            result={result}
            onClose={() => {}}
          />
        ))}
        {!searching && !error && visibleData?.results.length === 0 && (
          <Alert variant="outlined" severity="info">
            No results for your search - consider tweaking your search filters.
          </Alert>
        )}
      </Box>
    </GridPage>
  );
};
